// MedigapBirthdayRule — state-by-state Medigap Birthday Rule
// reference + per-beneficiary window calculator.
//
// Closes audit response §18 Phase 9 Medicare row "Birthday rule /
// LIS copay state tracker." The Birthday Rule states let a Medigap
// policyholder switch to an equal-or-lesser plan without medical
// underwriting during a defined window around their birthday — but
// the rules vary wildly by state. Most operators keep this in a
// Notion doc; surfacing it inside the workspace removes the
// lookup tax.
//
// IMPORTANT: state insurance rules change. The table below reflects
// rules current as of 2026; the component prominently caveats
// verification against the state DOI before any specific advice.

import React from 'react';

// State-by-state Medigap Birthday Rule lookup.
//
// Notation:
//   anchor     — 'birthday' | 'anniversary' | 'continuous' | 'other'
//   window     — { before_days, after_days, duration_days }
//                Window is computed from the anchor date inclusive.
//   guaranteed_issue — what the rule actually buys you (e.g. swap
//                       to lesser benefit plan, swap within company)
//   notes      — short operator-facing context
//
// Rules omitted when state lacks a Birthday Rule but has a different
// guaranteed-issue path (CT, MA, NY have continuous open enrollment;
// that's a different conversation).
const MEDIGAP_BIRTHDAY_RULES = {
  CA: {
    state: 'California',
    anchor: 'birthday',
    window: { before_days: 0, after_days: 60, duration_days: 61 },
    guaranteed_issue: 'Switch to any Medigap plan with EQUAL or LESSER benefits with any carrier.',
    notes: 'Most generous window in the country — any carrier, any same-or-lesser plan letter.',
    last_verified: '2025-01',
  },
  ID: {
    state: 'Idaho',
    anchor: 'birthday',
    window: { before_days: 0, after_days: 63, duration_days: 64 },
    guaranteed_issue: 'Switch to a Medigap plan with EQUAL or LESSER benefits with the same carrier.',
    notes: 'Same-carrier only — narrower than CA.',
    last_verified: '2025-01',
  },
  IL: {
    state: 'Illinois',
    anchor: 'birthday',
    window: { before_days: 0, after_days: 45, duration_days: 46 },
    guaranteed_issue: 'Switch to EQUAL or LESSER plan with the same carrier. Ages 65-75 only.',
    notes: 'Age-restricted: only beneficiaries 65-75. Same-carrier requirement.',
    last_verified: '2025-01',
  },
  KY: {
    state: 'Kentucky',
    anchor: 'birthday',
    window: { before_days: 0, after_days: 60, duration_days: 61 },
    guaranteed_issue: 'Switch to EQUAL or LESSER plan with the same carrier.',
    notes: 'Same-carrier only.',
    last_verified: '2025-01',
  },
  LA: {
    state: 'Louisiana',
    anchor: 'birthday',
    window: { before_days: 30, after_days: 63, duration_days: 94 },
    guaranteed_issue: 'Switch to EQUAL or LESSER plan with the same carrier.',
    notes: 'Window starts 30 days BEFORE the birthday (uncommon).',
    last_verified: '2025-01',
  },
  MD: {
    state: 'Maryland',
    anchor: 'birthday',
    window: { before_days: 30, after_days: 30, duration_days: 61 },
    guaranteed_issue: 'Switch to EQUAL or LESSER plan with the same carrier.',
    notes: 'Two-sided window — 30 days each side of birthday.',
    last_verified: '2025-01',
  },
  NV: {
    state: 'Nevada',
    anchor: 'birthday',
    window: { before_days: 60, after_days: 60, duration_days: 121 },
    guaranteed_issue: 'Switch to EQUAL or LESSER plan with any carrier.',
    notes: 'Two-sided 60-day window — most generous after California.',
    last_verified: '2025-01',
  },
  OK: {
    state: 'Oklahoma',
    anchor: 'birthday',
    window: { before_days: 0, after_days: 60, duration_days: 61 },
    guaranteed_issue: 'Switch to EQUAL or LESSER plan with any carrier.',
    notes: 'Any carrier, same-or-lesser benefit letter.',
    last_verified: '2025-01',
  },
  OR: {
    state: 'Oregon',
    anchor: 'birthday',
    window: { before_days: 0, after_days: 30, duration_days: 31 },
    guaranteed_issue: 'Switch to EQUAL or LESSER plan with the same carrier.',
    notes: 'Shortest birthday window.',
    last_verified: '2025-01',
  },
  MO: {
    state: 'Missouri',
    anchor: 'anniversary',
    window: { before_days: 30, after_days: 30, duration_days: 61 },
    guaranteed_issue: 'Switch to the SAME plan letter with a different carrier (anniversary rule).',
    notes: 'Anniversary of POLICY (not birthday). Same plan letter only — designed for shopping carriers.',
    last_verified: '2025-01',
  },
};

// States with a different guaranteed-issue path — operator should
// know these exist even though they aren't strict "birthday rule"
// states. Flagged separately in the UI.
const CONTINUOUS_ISSUE_NOTES = {
  CT: 'Continuous open enrollment — no birthday window needed.',
  MA: 'Continuous open enrollment + community rating.',
  NY: 'Continuous open enrollment + community rating.',
  ME: 'Continuous OE if enrolled in Plan A; can switch carriers any time.',
  WA: 'Annual 90-day OE in the Birthday month is being implemented (verify current status).',
};

const STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY',
  'LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND',
  'OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC',
];

function fmtDate(d) {
  if (!d) return '—';
  try {
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return '—'; }
}

function daysBetween(a, b) {
  return Math.round((b.getTime() - a.getTime()) / 86400000);
}

export default function MedigapBirthdayRule() {
  const [stateCode, setStateCode]     = React.useState('CA');
  const [birthDate, setBirthDate]     = React.useState('');

  const rule = MEDIGAP_BIRTHDAY_RULES[stateCode] || null;
  const continuous = CONTINUOUS_ISSUE_NOTES[stateCode] || null;

  // Compute this year's window when birthDate + rule are present.
  const today = React.useMemo(() => new Date(), []);
  const window = React.useMemo(() => {
    if (!rule || !birthDate || !/^\d{4}-\d{2}-\d{2}$/.test(birthDate)) return null;
    const [, m, d] = birthDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    // Current-year birthday or anniversary.
    const yr = today.getFullYear();
    const anchorThisYear = new Date(Date.UTC(yr, Number(m) - 1, Number(d), 12));
    const anchorNextYear = new Date(Date.UTC(yr + 1, Number(m) - 1, Number(d), 12));
    // Figure out which anchor is the "next" or "current" one.
    const windowStartThis = new Date(anchorThisYear.getTime() - rule.window.before_days * 86400000);
    const windowEndThis   = new Date(anchorThisYear.getTime() + rule.window.after_days * 86400000);
    const isInWindowThis = today >= windowStartThis && today <= windowEndThis;
    if (isInWindowThis) {
      const daysUntilClose = daysBetween(today, windowEndThis);
      return {
        status: 'open',
        anchor_date: anchorThisYear,
        start: windowStartThis,
        end: windowEndThis,
        days_until_close: daysUntilClose,
      };
    }
    // Otherwise, calculate next year's window.
    const isThisYearOver = today > windowEndThis;
    const targetAnchor = isThisYearOver ? anchorNextYear : anchorThisYear;
    const targetStart = new Date(targetAnchor.getTime() - rule.window.before_days * 86400000);
    const targetEnd   = new Date(targetAnchor.getTime() + rule.window.after_days * 86400000);
    return {
      status: 'upcoming',
      anchor_date: targetAnchor,
      start: targetStart,
      end: targetEnd,
      days_until_open: daysBetween(today, targetStart),
    };
  }, [rule, birthDate, today]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden max-w-none mx-auto">
      <div className="bg-gradient-to-r from-amber-600 to-amber-700 text-white px-5 py-4">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-amber-200/80">Medicare</div>
        <h2 className="text-xl font-bold">Medigap Birthday Rule</h2>
        <p className="text-xs text-amber-100/80 mt-0.5">State-by-state guaranteed-issue window lookup + per-client window math.</p>
      </div>

      <div className="p-5 space-y-4">
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900">
          <strong>State rules change.</strong> The table reflects rules current as of late 2025 / early 2026. <span className="italic">Always verify against the state DOI before quoting a specific timeline to a client.</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-sm">
            <span className="text-slate-700 font-medium">Client's state</span>
            <select
              value={stateCode}
              onChange={e => setStateCode(e.target.value)}
              className="w-full mt-1 px-3 py-2 border-2 border-slate-200 rounded-lg text-sm focus:border-amber-500 focus:outline-none bg-white"
            >
              {STATES.map(s => (
                <option key={s} value={s}>{s} {MEDIGAP_BIRTHDAY_RULES[s] ? `· ${MEDIGAP_BIRTHDAY_RULES[s].state}` : CONTINUOUS_ISSUE_NOTES[s] ? `· ${s} (other rule)` : ''}</option>
              ))}
            </select>
          </label>

          <label className="text-sm">
            <span className="text-slate-700 font-medium">Birth date (optional)</span>
            <input
              type="date"
              value={birthDate}
              onChange={e => setBirthDate(e.target.value)}
              className="w-full mt-1 px-3 py-2 border-2 border-slate-200 rounded-lg text-sm focus:border-amber-500 focus:outline-none"
            />
            <div className="text-[11px] text-slate-500 mt-1">Optional — only used to compute the window dates.</div>
          </label>
        </div>

        {/* State rule card */}
        {rule && (
          <div className="border-2 border-amber-300 bg-amber-50 rounded-lg p-4">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-amber-800">Birthday Rule state</div>
                <div className="text-lg font-bold text-amber-900 mt-0.5">{rule.state}</div>
              </div>
              <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-200 text-amber-900 rounded px-2 py-1">
                Last verified {rule.last_verified}
              </span>
            </div>
            <div className="mt-3 space-y-2 text-sm text-amber-900">
              <div>
                <span className="font-semibold">Anchor:</span> {rule.anchor === 'birthday' ? "Beneficiary's birthday" : rule.anchor === 'anniversary' ? "Policy anniversary" : rule.anchor}
              </div>
              <div>
                <span className="font-semibold">Window:</span> {rule.window.before_days > 0 ? `${rule.window.before_days} days before → ` : 'starts on '}
                {rule.anchor === 'birthday' ? 'birthday' : 'anniversary'} + {rule.window.after_days} days after ({rule.window.duration_days} days total)
              </div>
              <div>
                <span className="font-semibold">Guaranteed issue:</span> {rule.guaranteed_issue}
              </div>
              {rule.notes && (
                <div className="text-xs italic opacity-90 pt-1 border-t border-amber-200">
                  {rule.notes}
                </div>
              )}
            </div>
          </div>
        )}

        {!rule && continuous && (
          <div className="border-2 border-sky-300 bg-sky-50 rounded-lg p-4">
            <div className="text-[10px] font-bold uppercase tracking-wider text-sky-800">Not a Birthday Rule state</div>
            <div className="text-lg font-bold text-sky-900 mt-0.5">{stateCode}</div>
            <div className="mt-2 text-sm text-sky-900">{continuous}</div>
          </div>
        )}

        {!rule && !continuous && (
          <div className="border border-slate-200 bg-slate-50 rounded-lg p-4 text-sm text-slate-600">
            <strong>{stateCode}</strong> has no Birthday Rule. Medigap underwriting applies for any plan switch outside the beneficiary's initial enrollment period or a federal guaranteed-issue trigger (loss of employer coverage, Medicare Advantage Trial Right, etc.).
          </div>
        )}

        {/* Per-beneficiary window math */}
        {window && rule && (
          <div className={`border-2 rounded-lg p-4 ${
            window.status === 'open'
              ? 'border-emerald-300 bg-emerald-50'
              : 'border-slate-200 bg-white'
          }`}>
            <div className="text-[10px] font-bold uppercase tracking-wider mb-2 opacity-70">
              {window.status === 'open' ? '✓ Window is OPEN now' : 'Upcoming window'}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">{rule.anchor === 'anniversary' ? 'Anniversary' : 'Birthday'}</div>
                <div className="font-bold mt-0.5">{fmtDate(window.anchor_date)}</div>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">Window opens</div>
                <div className="font-bold mt-0.5">{fmtDate(window.start)}</div>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">Window closes</div>
                <div className="font-bold mt-0.5">{fmtDate(window.end)}</div>
              </div>
            </div>
            {window.status === 'open' && (
              <div className="mt-3 text-sm font-semibold text-emerald-900">
                {window.days_until_close === 0
                  ? 'Last day — submit changes today.'
                  : `${window.days_until_close} day${window.days_until_close === 1 ? '' : 's'} until the window closes.`}
              </div>
            )}
            {window.status === 'upcoming' && (
              <div className="mt-3 text-sm font-semibold text-slate-700">
                {window.days_until_open <= 0 ? 'Opens today.' : `Opens in ${window.days_until_open} day${window.days_until_open === 1 ? '' : 's'}.`}
              </div>
            )}
          </div>
        )}

        {/* Reference table */}
        <details className="text-xs">
          <summary className="cursor-pointer text-slate-600 font-medium hover:text-slate-900">
            Show all Birthday Rule states ({Object.keys(MEDIGAP_BIRTHDAY_RULES).length})
          </summary>
          <div className="mt-2 bg-slate-50 border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 text-slate-700">
                <tr>
                  <th className="px-2 py-1.5 text-left">State</th>
                  <th className="px-2 py-1.5 text-left">Anchor</th>
                  <th className="px-2 py-1.5 text-right">Window</th>
                  <th className="px-2 py-1.5 text-left">Switch scope</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {Object.entries(MEDIGAP_BIRTHDAY_RULES).map(([code, r]) => (
                  <tr key={code} className={code === stateCode ? 'bg-amber-50 font-bold' : ''}>
                    <td className="px-2 py-1.5">{code} · {r.state}</td>
                    <td className="px-2 py-1.5">{r.anchor}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">
                      {r.window.before_days > 0 && `-${r.window.before_days} → `}+{r.window.after_days}d
                    </td>
                    <td className="px-2 py-1.5 text-[11px]">{r.guaranteed_issue.length > 90 ? r.guaranteed_issue.slice(0, 90) + '…' : r.guaranteed_issue}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>

        <div className="text-xs text-slate-500 leading-relaxed border-t border-slate-100 pt-3">
          <strong>Operator note:</strong> Even when the state has a Birthday Rule, the SUBMISSION typically needs to land within the window — carriers' underwriting departments backlog at peak times. Don't promise a swap can close on day 60 of a 60-day window. Aim to submit by day 30 to leave room for paperwork.
        </div>
      </div>
    </div>
  );
}
